import { Component, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { LucideIconsModule } from '../../shared/lucide-icons.module';
import { CountrySelectorComponent } from '../../shared/components/country-selector/country-selector.component';
import { ImageWithSkeletonComponent } from '../../shared/components/image-with-skeleton/image-with-skeleton.component';
import { PROPERTY_TYPE_OPTIONS, PropertyTypeValue } from '../../shared/data/property-types';
import { selectIsAuthenticated } from '../../store/auth/auth.selectors';

type LandingListing = {
    id: string;
    badge?: string;
    title: string;
    subtitle: string;
    description: string;
    location: string;
    imageUrl: string;
    imageAlt: string;
    meta: Array<{ icon: string; text: string }>;
};

@Component({
    selector: 'app-landing-page',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, LucideIconsModule, CountrySelectorComponent, ImageWithSkeletonComponent],
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss']
})
export class LandingPageComponent {
    private router = inject(Router);
    private store = inject(Store);

    readonly isAuthenticated$ = this.store.select(selectIsAuthenticated);

    private readonly localImagery = {
        hero: 'assets/imagery/avif/hero-building.avif?v=20260329',
        strip: 'assets/imagery/avif/featured-3.avif?v=20260329',
        cta: 'assets/imagery/avif/cta-interior.avif?v=20260329',
        featured1: 'assets/imagery/avif/featured-1.avif?v=20260329',
        featured2: 'assets/imagery/avif/featured-2.avif?v=20260329',
        featured3: 'assets/imagery/avif/featured-3.avif?v=20260329'
    } as const;

    heroImage = this.localImagery.hero;
    heroStripImage = this.localImagery.strip;
    ctaImage = this.localImagery.cta;

    readonly propertyTypeOptions = PROPERTY_TYPE_OPTIONS;
    readonly bedroomOptions = [1, 2, 3, 4, 5, 6];
    readonly bathroomOptions = [1, 2, 3, 4, 5, 6];

    searchOverlayVisible = false;
    searchOverlayOpen = false;
    searchModel: {
        search: string;
        country: string;
        propertyType: PropertyTypeValue;
        bedrooms: number | null;
        bathrooms: number | null;
        minPrice: number | null;
        maxPrice: number | null;
    } = {
        search: '',
        country: '',
        propertyType: 'any',
        bedrooms: null,
        bathrooms: null,
        minPrice: null,
        maxPrice: null
    };

    readonly featuredListings: LandingListing[] = [
        {
            id: 'showcase-1',
            badge: 'Featured',
            title: 'Investor-ready apartments',
            subtitle: 'Verified partners • Flexible plans',
            description: 'Explore modern apartments with transparent pricing and payment plans from trusted developers.',
            location: 'Prime city districts and high-demand neighborhoods',
            imageUrl: this.localImagery.featured1,
            imageAlt: 'Modern apartment interior',
            meta: [
                { icon: 'shield-check', text: 'Verified' },
                { icon: 'calendar-clock', text: 'Payment plans' },
                { icon: 'map-pin', text: 'City-first' },
                { icon: 'sparkles', text: 'Premium finish' }
            ]
        },
        {
            id: 'showcase-2',
            badge: 'Curated',
            title: 'Family residences & villas',
            subtitle: 'Space, privacy, and lifestyle',
            description: 'A refined selection of residences designed for comfort—ideal for families and long-term investors.',
            location: 'Suburban communities and coastal escapes',
            imageUrl: this.localImagery.featured2,
            imageAlt: 'Contemporary residential building',
            meta: [
                { icon: 'home', text: 'Lifestyle' },
                { icon: 'users', text: 'Family-ready' },
                { icon: 'wallet', text: 'Smart value' },
                { icon: 'star', text: 'Curated' }
            ]
        },
        {
            id: 'showcase-3',
            badge: 'New',
            title: 'Investment opportunities',
            subtitle: 'New launches • High potential',
            description: 'Discover new developments and opportunities selected for strong fundamentals and market demand.',
            location: 'Growing districts and emerging hotspots',
            imageUrl: this.localImagery.featured3,
            imageAlt: 'Modern property exterior',
            meta: [
                { icon: 'trending-up', text: 'Growth' },
                { icon: 'building-2', text: 'New builds' },
                { icon: 'badge-percent', text: 'Value' },
                { icon: 'layers', text: 'Projects' }
            ]
        }
    ];

    trackByListingId(_index: number, listing: LandingListing): string {
        return listing.id;
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.searchOverlayVisible) this.closeSearchOverlay();
    }

    openSearchOverlay(): void {
        if (this.searchOverlayVisible) return;
        this.searchOverlayVisible = true;
        // Trigger CSS transitions on next tick.
        setTimeout(() => {
            this.searchOverlayOpen = true;
        }, 0);
        if (typeof document !== 'undefined') {
            document.body.style.overflow = 'hidden';
        }
    }

    closeSearchOverlay(): void {
        this.searchOverlayOpen = false;
        setTimeout(() => {
            this.searchOverlayVisible = false;
            if (typeof document !== 'undefined') {
                document.body.style.overflow = '';
            }
        }, 180);
    }

    submitSearch(): void {
        const q: Record<string, string> = {};
        const search = this.searchModel.search?.trim();
        const country = this.searchModel.country?.trim();
        const type = this.searchModel.propertyType;

        if (search) q['search'] = search;
        if (country) q['country'] = country;
        if (type && type !== 'any') q['type'] = type;
        if (this.searchModel.bedrooms) q['bedrooms'] = String(this.searchModel.bedrooms);
        if (this.searchModel.bathrooms) q['bathrooms'] = String(this.searchModel.bathrooms);
        if (this.searchModel.minPrice !== null && this.searchModel.minPrice !== undefined && this.searchModel.minPrice !== ('' as any)) {
            q['minPrice'] = String(this.searchModel.minPrice);
        }
        if (this.searchModel.maxPrice !== null && this.searchModel.maxPrice !== undefined && this.searchModel.maxPrice !== ('' as any)) {
            q['maxPrice'] = String(this.searchModel.maxPrice);
        }
        q['openFilters'] = '1';

        this.closeSearchOverlay();
        this.router.navigate(['/properties'], { queryParams: q });
    }

    onHeroImageError(kind: 'hero' | 'strip' | 'cta'): void {
        if (kind === 'hero') this.heroImage = this.localImagery.hero;
        if (kind === 'strip') this.heroStripImage = this.localImagery.strip;
        if (kind === 'cta') this.ctaImage = this.localImagery.cta;
    }

    onListingImageError(listing: LandingListing): void {
        const fallback =
            listing.id === 'showcase-1'
                ? this.localImagery.featured1
                : listing.id === 'showcase-2'
                    ? this.localImagery.featured2
                    : listing.id === 'showcase-3'
                        ? this.localImagery.featured3
                        : null;

        if (fallback && listing.imageUrl !== fallback) {
            listing.imageUrl = fallback;
        }
    }
}
